/**
 * AccessMap Utility Functions
 * 
 * Centralized utilities for handling accessMap operations for both Projects and Collections.
 * This module provides a single source of truth for all accessMap-related operations.
 * 
 * Backend Structure (NEW FORMAT):
 * {
 *   userRoles: [
 *     { userId: "username", roletype: "OWNER" },
 *     { userId: "username2", roletype: "CONTRIBUTOR" }
 *   ],
 *   roleAccessList: {
 *     OWNER: "RESTRICTED",
 *     CONTRIBUTOR: "RESTRICTED",
 *     VIEWER: "PUBLIC"
 *   }
 * }
 */

import {
  DEFAULT_ROLE_ACCESS_LIST,
  PUBLIC_ROLE_ACCESS_LIST,
  ROLE_TYPES,
  ACCESS_TYPES,
  PUBLIC_ACCESS_OPTIONS,
  uiOptionToPublicAccessPolicy,
  publicAccessPolicyToUiOption,
} from './accessMapConstants';

// ============================================================================
// DATA TRANSFORMATION FUNCTIONS
// ============================================================================

const objectIdRegex = /^[a-f0-9]{24}$/i;

const normalizeUserRolesArray = (userRolesInput) => {
  if (Array.isArray(userRolesInput)) {
    return userRolesInput
      .map((ur) => {
        const normalized = {
          userId:
            typeof ur?.userId === "string"
              ? ur.userId
              : ur?.userId != null
              ? String(ur.userId)
              : "",
          roletype:
            typeof ur?.roletype === "string" && ur.roletype
              ? ur.roletype
              : typeof ur === "string" && ur
              ? ur
              : "VIEWER",
        };
        // Preserve username field if it exists (backend provides it)
        if (ur?.username) {
          normalized.username = String(ur.username);
        } else if (normalized.userId && !objectIdRegex.test(String(normalized.userId).trim())) {
          // If we don't have an explicit username but userId looks like a username
          // (i.e., is NOT a MongoDB ObjectId), treat userId as username.
          normalized.username = String(normalized.userId).trim();
        }
        // Preserve principalKey field if it exists
        if (ur?.principalKey) {
          normalized.principalKey = String(ur.principalKey);
        }
        return normalized;
      })
      .filter((ur) => ur.userId);
  }

  if (userRolesInput && typeof userRolesInput === "object") {
    return Object.entries(userRolesInput)
      .map(([userId, value]) => ({
        userId,
        roletype:
          typeof value === "string"
            ? value
            : typeof value?.roletype === "string"
            ? value.roletype
            : "VIEWER",
      }))
      .filter((ur) => ur.userId);
  }

  return [];
};

/**
 * Derive roleAccessList from backend publicAccessPolicy.
 * VIEWER/CONTRIBUTOR → public roles; DEFAULT/NONE/empty → all restricted.
 */
const roleAccessListFromPublicAccessPolicy = (publicAccessPolicy) => {
  const list = { ...DEFAULT_ROLE_ACCESS_LIST };
  if (!publicAccessPolicy || !Array.isArray(publicAccessPolicy)) {
    return list;
  }
  const set = new Set(
    publicAccessPolicy.map((role) => String(role || '').trim().toUpperCase())
  );
  if (set.has(ROLE_TYPES.CONTRIBUTOR)) {
    list[ROLE_TYPES.CONTRIBUTOR] = ACCESS_TYPES.PUBLIC;
    list[ROLE_TYPES.VIEWER] = ACCESS_TYPES.PUBLIC;
  } else if (set.has(ROLE_TYPES.VIEWER)) {
    list[ROLE_TYPES.VIEWER] = ACCESS_TYPES.PUBLIC;
  }
  return list;
};

/**
 * Resolve UI option from backend/FE accessMap.
 * Prefer non-empty publicAccessPolicy.
 * Empty policy from older records → Default.
 */
const resolveUiOptionFromAccessMap = (accessMap) => {
  if (!accessMap) {
    return PUBLIC_ACCESS_OPTIONS.DEFAULT;
  }

  if (Array.isArray(accessMap.publicAccessPolicy)) {
    if (accessMap.publicAccessPolicy.length > 0) {
      return publicAccessPolicyToUiOption(accessMap.publicAccessPolicy);
    }
    // Empty policy → Default (legacy records).
    return PUBLIC_ACCESS_OPTIONS.DEFAULT;
  }

  if (accessMap.roleAccessList) {
    const contributorPublic =
      accessMap.roleAccessList[ROLE_TYPES.CONTRIBUTOR] === ACCESS_TYPES.PUBLIC;
    const viewerPublic =
      accessMap.roleAccessList[ROLE_TYPES.VIEWER] === ACCESS_TYPES.PUBLIC;
    if (contributorPublic) return PUBLIC_ACCESS_OPTIONS.EDIT;
    if (viewerPublic) return PUBLIC_ACCESS_OPTIONS.VIEW;
  }

  return PUBLIC_ACCESS_OPTIONS.DEFAULT;
};

/**
 * Parses accessMap received from backend and returns a normalized copy.
 * Canonical wire field is publicAccessPolicy (DEFAULT|NONE|VIEWER|CONTRIBUTOR).
 * Empty policy from older records is treated as Default.
 */
export const parseAccessMapFromBackend = (accessMap) => {
  const fallbackRoleAccessList = { ...DEFAULT_ROLE_ACCESS_LIST };

  if (!accessMap) {
    return {
      userRoles: [],
      roleAccessList: { ...fallbackRoleAccessList },
      publicAccessPolicy: uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.DEFAULT),
    };
  }

  const sourceList =
    Array.isArray(accessMap.userRoles) && accessMap.userRoles.length
      ? accessMap.userRoles
      : accessMap.userRoleMap;
  const normalizedUserRoles = normalizeUserRolesArray(sourceList);
  const userRoleMap = normalizedUserRoles.reduce((acc, ur) => {
    const key = ur.username || ur.userId;
    if (key) acc[key] = ur.roletype;
    return acc;
  }, {});

  const uiOption = resolveUiOptionFromAccessMap(accessMap);
  const publicAccessPolicy = uiOptionToPublicAccessPolicy(uiOption);
  const roleAccessList =
    uiOption === PUBLIC_ACCESS_OPTIONS.VIEW || uiOption === PUBLIC_ACCESS_OPTIONS.EDIT
      ? roleAccessListFromPublicAccessPolicy(publicAccessPolicy)
      : { ...fallbackRoleAccessList };

  return {
    userRoles: normalizedUserRoles,
    userRoleMap,
    roleAccessList,
    publicAccessPolicy,
  };
};

/**
 * Formats accessMap for sending to backend.
 * Writes publicAccessPolicy with [DEFAULT] / [NONE] / [VIEWER] / [CONTRIBUTOR].
 */
export const formatAccessMapForBackend = (accessMap) => {
  if (!accessMap) {
    return {
      userRoles: [],
      publicAccessPolicy: uiOptionToPublicAccessPolicy(PUBLIC_ACCESS_OPTIONS.PROTECTED),
    };
  }

  const sourceList =
    Array.isArray(accessMap.userRoles) && accessMap.userRoles.length
      ? accessMap.userRoles
      : accessMap.userRoleMap;

  const uiOption = resolveUiOptionFromAccessMap(accessMap);

  return {
    userRoles: normalizeUserRolesArray(sourceList),
    publicAccessPolicy: uiOptionToPublicAccessPolicy(uiOption),
  };
};

/**
 * Prepares accessMap.userRoles for backend comparison/logging.
 *
 * Context:
 * - The UI frequently normalizes accessMap so that userId holds the username
 *   (for easier display and editing) and also sets the username field.
 * - The backend expects either:
 *   - userId = stable database ID + username = display name, OR
 *   - userId empty/null + username = display name (it then resolves IDs itself).
 *
 * If we send userId=username, the backend cannot distinguish IDs from usernames
 * when computing diffs, which leads to noisy logs (owners removed + re‑added).
 *
 * Rule:
 * - If userId is present but clearly NOT a Mongo ObjectId and matches the
 *   username (or username is empty), treat it as a pure username:
 *   - ensure username is set
 *   - clear userId so backend will resolve it to a stable ID.
 */
export const prepareAccessMapUserRolesForBackend = (accessMap) => {
  if (!accessMap || !Array.isArray(accessMap.userRoles)) {
    return accessMap;
  }

  const cleanedUserRoles = accessMap.userRoles.map((ur) => {
    if (!ur) return ur;

    const next = { ...ur };
    const rawUserId = next.userId != null ? String(next.userId).trim() : "";
    const rawUsername = next.username != null ? String(next.username).trim() : "";

    // If userId looks like a username (non‑ObjectId), treat it as username only.
    if (rawUserId && !objectIdRegex.test(rawUserId)) {
      if (!rawUsername) {
        next.username = rawUserId;
      }
      next.userId = null;
    }

    return next;
  });

  return {
    ...accessMap,
    userRoles: cleanedUserRoles,
  };
};

// ============================================================================
// USER ROLE ACCESS FUNCTIONS
// ============================================================================

/**
 * Gets the role for a specific user from accessMap
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to look up
 * @returns {string|null} - Role type (OWNER, CONTRIBUTOR, VIEWER) or null
 */
export const getUserRole = (accessMap, username, currentUser = null) => {
  if (!accessMap?.userRoles || !username) return null;
  
  // Ensure username is a string and trim whitespace
  const normalizedUsername = String(username).trim();
  if (!normalizedUsername) return null;
  
  // Get current user ID if available
  const currentUserId = currentUser?.id ? String(currentUser.id).trim() : null;
  const currentUserUsername = currentUser?.username ? String(currentUser.username).trim() : null;
  
  // Find exact match (case-sensitive, as usernames are case-sensitive)
  // Check both userId and username fields since backend provides both
  const userRole = accessMap.userRoles.find(ur => {
    if (!ur || !ur.userId) return false;
    
    // Compare as strings, ensuring both are trimmed
    const userId = String(ur.userId).trim();
    const username = ur.username ? String(ur.username).trim() : null;
    
    // First check: direct username match (check username field if it exists)
    if (username && username === normalizedUsername) {
      return true;
    }
    
    // Second check: userId matches username (for cases where userId is already a username)
    if (userId === normalizedUsername) {
      return true;
    }
    
    // Third check: if userId is an ObjectId and matches currentUser.id, 
    // and the username we're looking for matches currentUser.username
    if (currentUserId && currentUserUsername && normalizedUsername === currentUserUsername) {
      const isObjectId = objectIdRegex.test(userId);
      if (isObjectId && userId === currentUserId) {
        return true;
      }
    }
    
    return false;
  });
  
  return userRole ? userRole.roletype : null;
};

/**
 * Checks if a user is an owner
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {boolean} - True if user is an owner
 */
export const isOwner = (accessMap, username) => {
  return getUserRole(accessMap, username) === ROLE_TYPES.OWNER;
};

/**
 * Checks if a user is a contributor
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {boolean} - True if user is a contributor
 */
export const isContributor = (accessMap, username) => {
  return getUserRole(accessMap, username) === ROLE_TYPES.CONTRIBUTOR;
};

/**
 * Checks if a user is a viewer
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {boolean} - True if user is a viewer
 */
export const isViewer = (accessMap, username) => {
  return getUserRole(accessMap, username) === ROLE_TYPES.VIEWER;
};

/**
 * Checks if a user has any explicit role
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @param {Object} currentUser - Optional current user object with id and username
 * @returns {boolean} - True if user has any explicit role
 */
export const hasExplicitRole = (accessMap, username, currentUser = null) => {
  const role = getUserRole(accessMap, username, currentUser);
  return role === ROLE_TYPES.OWNER || role === ROLE_TYPES.CONTRIBUTOR || role === ROLE_TYPES.VIEWER;
};

// ============================================================================
// ROLE MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Gets array of usernames for a specific role
 * @param {Object} accessMap - The accessMap object
 * @param {string} roleType - The role type (OWNER, CONTRIBUTOR, VIEWER)
 * @returns {Array<string>} - Array of usernames with that role
 */
export const getUsersByRole = (accessMap, roleType) => {
  if (!accessMap?.userRoles || !roleType) return [];
  
  return accessMap.userRoles
    .filter(ur => ur.roletype === roleType)
    .map(ur => ur.userId);
};

/**
 * Adds or updates a user's role in accessMap (returns new accessMap, does not mutate)
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to add/update
 * @param {string} roleType - The role type (OWNER, CONTRIBUTOR, VIEWER)
 * @returns {Object} - New accessMap with updated user role
 */
export const addUserRole = (accessMap, username, roleType) => {
  if (!username || !roleType) return accessMap;
  
  const normalized = parseAccessMapFromBackend(accessMap);
  
  // Check if user already exists
  const existingIndex = normalized.userRoles.findIndex(ur => ur.userId === username);
  
  if (existingIndex >= 0) {
    // Update existing user's role
    normalized.userRoles[existingIndex] = { userId: username, roletype: roleType };
  } else {
    // Add new user
    normalized.userRoles.push({ userId: username, roletype: roleType });
  }
  
  return normalized;
};

/**
 * Removes a user from accessMap (returns new accessMap, does not mutate)
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to remove
 * @returns {Object} - New accessMap without the user
 */
export const removeUserRole = (accessMap, username) => {
  if (!username) return accessMap;
  
  const normalized = parseAccessMapFromBackend(accessMap);
  
  normalized.userRoles = normalized.userRoles.filter(ur => ur.userId !== username);
  
  return normalized;
};

/**
 * Sets the entire userRoles array (returns new accessMap, does not mutate)
 * @param {Object} accessMap - The accessMap object
 * @param {Array} userRolesArray - Array of {userId, roletype} objects
 * @returns {Object} - New accessMap with updated userRoles
 */
export const setUserRoles = (accessMap, userRolesArray) => {
  const normalized = parseAccessMapFromBackend(accessMap);
  
  normalized.userRoles = Array.isArray(userRolesArray) ? [...userRolesArray] : [];
  
  return normalized;
};

// ============================================================================
// VISIBILITY/ACCESS TYPE FUNCTIONS
// ============================================================================

/**
 * Checks if entity has public access (VIEW or EDIT mode, or legacy PUBLIC roles)
 * @param {Object} accessMap - The accessMap object
 * @returns {boolean} - True if publicly viewable/editable
 */
export const isPublic = (accessMap) => {
  if (!accessMap) return false;

  const uiOption = resolveUiOptionFromAccessMap(accessMap);
  if (
    uiOption === PUBLIC_ACCESS_OPTIONS.VIEW ||
    uiOption === PUBLIC_ACCESS_OPTIONS.EDIT
  ) {
    return true;
  }
  if (
    uiOption === PUBLIC_ACCESS_OPTIONS.PROTECTED ||
    uiOption === PUBLIC_ACCESS_OPTIONS.NONE ||
    uiOption === PUBLIC_ACCESS_OPTIONS.DEFAULT
  ) {
    return false;
  }

  if (!accessMap?.roleAccessList || typeof accessMap.roleAccessList !== 'object') return false;

  const roleAccessList = accessMap.roleAccessList;
  const ownerAccess = String(roleAccessList[ROLE_TYPES.OWNER] || '').trim();
  const contributorAccess = String(roleAccessList[ROLE_TYPES.CONTRIBUTOR] || '').trim();
  const viewerAccess = String(roleAccessList[ROLE_TYPES.VIEWER] || '').trim();

  return ownerAccess === ACCESS_TYPES.PUBLIC ||
         contributorAccess === ACCESS_TYPES.PUBLIC ||
         viewerAccess === ACCESS_TYPES.PUBLIC;
};

/**
 * Gets visibility as 'public' or 'private'
 * A project/collection is public if ANY role (OWNER, CONTRIBUTOR, or VIEWER) is set to PUBLIC
 * @param {Object} accessMap - The accessMap object
 * @returns {string} - 'public' if ANY role is PUBLIC, otherwise 'private'
 */
export const getVisibility = (accessMap) => {
  if (!accessMap?.roleAccessList) return 'private';
  
  // A project/collection is public when ANY role is set to PUBLIC
  const roleAccessList = accessMap.roleAccessList;
  const hasPublicRole = roleAccessList[ROLE_TYPES.OWNER] === ACCESS_TYPES.PUBLIC || 
                        roleAccessList[ROLE_TYPES.CONTRIBUTOR] === ACCESS_TYPES.PUBLIC || 
                        roleAccessList[ROLE_TYPES.VIEWER] === ACCESS_TYPES.PUBLIC;
  
  return hasPublicRole ? 'public' : 'private';
};

/**
 * Checks if entity has public access (any role is PUBLIC)
 * Alias for isPublic() for semantic clarity in filtering contexts
 * @param {Object} accessMap - The accessMap object
 * @returns {boolean} - True if any role has public access
 */
export const hasPublicAccess = (accessMap) => {
  return isPublic(accessMap);
};

// ============================================================================
// ROLE ACCESS MANAGEMENT FUNCTIONS
// ============================================================================

/**
 * Gets the access type for a specific role
 * @param {Object} accessMap - The accessMap object
 * @param {string} roleType - The role type (OWNER, CONTRIBUTOR, VIEWER)
 * @returns {string|null} - Access type (PUBLIC, RESTRICTED) or null
 */
export const getRoleAccess = (accessMap, roleType) => {
  if (!accessMap?.roleAccessList || !roleType) return null;
  
  return accessMap.roleAccessList[roleType] || null;
};

/**
 * Sets the access type for a specific role (returns new accessMap)
 * @param {Object} accessMap - The accessMap object
 * @param {string} roleType - The role type (OWNER, CONTRIBUTOR, VIEWER)
 * @param {string} accessType - The access type (PUBLIC, RESTRICTED)
 * @returns {Object} - New accessMap with updated roleAccessList
 */
export const setRoleAccess = (accessMap, roleType, accessType) => {
  if (!roleType || !accessType) return accessMap;
  
  const normalized = parseAccessMapFromBackend(accessMap);
  
  normalized.roleAccessList[roleType] = accessType;
  
  return normalized;
};

// ============================================================================
// PERMISSION CHECK FUNCTIONS
// ============================================================================

/**
 * Checks if a specific role is set to PUBLIC
 * @param {Object} accessMap - The accessMap object
 * @param {string} roleType - The role type (OWNER, CONTRIBUTOR, VIEWER)
 * @returns {boolean} - True if the role is PUBLIC
 */
export const isRolePublic = (accessMap, roleType) => {
  return getRoleAccess(accessMap, roleType) === ACCESS_TYPES.PUBLIC;
};

/**
 * Gets the highest public role level
 * Returns the highest role that is PUBLIC, or null if none are PUBLIC
 * Priority: OWNER > CONTRIBUTOR > VIEWER
 * @param {Object} accessMap - The accessMap object
 * @returns {string|null} - Highest public role (OWNER, CONTRIBUTOR, VIEWER) or null
 */
export const getHighestPublicRole = (accessMap) => {
  if (!accessMap?.roleAccessList) return null;
  
  const roleAccessList = accessMap.roleAccessList;
  
  if (roleAccessList[ROLE_TYPES.OWNER] === ACCESS_TYPES.PUBLIC) return ROLE_TYPES.OWNER;
  if (roleAccessList[ROLE_TYPES.CONTRIBUTOR] === ACCESS_TYPES.PUBLIC) return ROLE_TYPES.CONTRIBUTOR;
  if (roleAccessList[ROLE_TYPES.VIEWER] === ACCESS_TYPES.PUBLIC) return ROLE_TYPES.VIEWER;
  
  return null;
};

/**
 * Gets the effective access level for a user based on their explicit role and public access
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {string|null} - Effective role (OWNER, CONTRIBUTOR, VIEWER) or null
 */
export const getEffectiveAccessLevel = (accessMap, username) => {
  if (!accessMap || !username) return null;
  
  // First check explicit role assignment
  const explicitRole = getUserRole(accessMap, username);
  if (explicitRole) return explicitRole;
  
  // If no explicit role, check public access levels
  // Return the highest public role available
  return getHighestPublicRole(accessMap);
};

/**
 * Checks if a user can view the project/collection
 * User can view if:
 * - They have an explicit role (OWNER, CONTRIBUTOR, or VIEWER), OR
 * - VIEWER role is PUBLIC, OR
 * - CONTRIBUTOR role is PUBLIC, OR
 * - OWNER role is PUBLIC
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {boolean} - True if user can view
 */
export const canUserView = (accessMap, username) => {
  if (!accessMap || !username) return false;
  
  // If user has any explicit role, they can view
  if (hasExplicitRole(accessMap, username)) return true;
  
  // If any role is PUBLIC, anyone can view
  return isPublic(accessMap);
};

/**
 * Checks if a user can edit the project/collection.
 * Edit is allowed only when:
 * - User has explicit OWNER or CONTRIBUTOR role, OR
 * - User has no explicit role and public access level is Edit (everyone can edit).
 * Edit is disabled when:
 * - User has explicit VIEWER role (view-only, even if public access is Edit), OR
 * - User has no role and sees the item via Discovery with public access View (everyone can see, only assigned Owner/Contributor can edit).
 * @param {Object} accessMap - The accessMap object (from API or normalized)
 * @param {string} username - The username to check
 * @param {Object} [currentUser] - Optional current user (for getUserRole resolution)
 * @returns {boolean} - True if user can edit
 */
export const canUserEdit = (accessMap, username, currentUser = null) => {
  if (!accessMap || !username) return false;

  const explicitRole = getUserRole(accessMap, username, currentUser);

  // Explicit VIEWER can never edit (view-only)
  if (explicitRole === ROLE_TYPES.VIEWER) return false;

  // Explicit OWNER or CONTRIBUTOR can always edit
  if (explicitRole === ROLE_TYPES.OWNER || explicitRole === ROLE_TYPES.CONTRIBUTOR) return true;

  // No explicit role (user sees item on Discovery): can edit only when public access is Edit
  const parsed = parseAccessMapFromBackend(accessMap);
  const uiOption = resolveUiOptionFromAccessMap(parsed);
  if (uiOption === PUBLIC_ACCESS_OPTIONS.EDIT) {
    return true;
  }
  const contributorPublic = parsed?.roleAccessList?.[ROLE_TYPES.CONTRIBUTOR] === ACCESS_TYPES.PUBLIC;
  return contributorPublic === true;
};

/**
 * Checks if a user can control/manage the project/collection (owner-level access)
 * User can control if:
 * - They have an explicit OWNER role
 * 
 * Note: OWNER role is never PUBLIC, so only explicitly assigned owners can control.
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {boolean} - True if user can control
 */
export const canUserControl = (accessMap, username) => {
  if (!accessMap || !username) return false;
  
  // Only explicitly assigned owners can control
  return isOwner(accessMap, username);
};

// ============================================================================
// UI HELPER FUNCTIONS (NOT VALIDATION)
// ============================================================================

/**
 * Checks if a user is the last owner (useful for UI logic like disabling delete button)
 * @param {Object} accessMap - The accessMap object
 * @param {string} username - The username to check
 * @returns {boolean} - True if user is the only owner
 */
export const isLastOwner = (accessMap, username) => {
  const owners = getUsersByRole(accessMap, 'OWNER');
  return owners.length === 1 && owners[0] === username;
};

/**
 * Creates a new accessMap with default values.
 * Public access is stored only as publicAccessPolicy (DEFAULT|NONE|VIEWER|CONTRIBUTOR).
 *
 * @param {string} ownerUsername - The username of the owner
 * @param {string} visibility - 'public' or 'private' (default: 'private')
 * @param {string} [publicAccessOption] - PUBLIC_ACCESS_OPTIONS value.
 *   Defaults to Protected for private, View for public.
 * @returns {Object} - New accessMap object
 */
export function createAccessMap(ownerUsername, visibility = "private", publicAccessOption) {
  const fallbackOption =
    visibility === "public"
      ? PUBLIC_ACCESS_OPTIONS.VIEW
      : PUBLIC_ACCESS_OPTIONS.PROTECTED;

  let resolvedOption = publicAccessOption ?? fallbackOption;
  if (resolvedOption === PUBLIC_ACCESS_OPTIONS.NONE) {
    resolvedOption = PUBLIC_ACCESS_OPTIONS.PROTECTED;
  } else if (!Object.values(PUBLIC_ACCESS_OPTIONS).includes(resolvedOption)) {
    resolvedOption =
      publicAccessPolicyToUiOption([resolvedOption]) || fallbackOption;
  }

  const accessMap = {
    userRoles: [],
    userRoleMap: {},
    roleAccessList:
      visibility === "public"
        ? { ...PUBLIC_ROLE_ACCESS_LIST }
        : { ...DEFAULT_ROLE_ACCESS_LIST },
    publicAccessPolicy: uiOptionToPublicAccessPolicy(resolvedOption),
  };

  if (ownerUsername) {
    accessMap.userRoles.push({
      userId: ownerUsername,
      roletype: ROLE_TYPES.OWNER,
    });
    accessMap.userRoleMap[ownerUsername] = ROLE_TYPES.OWNER;
  }

  return accessMap;
}

/**
 * Converts a simple userRoles object to the new array format
 * Used when building accessMap from form data (e.g., creation modals)
 * @param {Object} userRolesObject - Object with username as key and role as value
 * @returns {Array} - Array of {userId, roletype} objects
 */
export const userRolesObjectToArray = (userRolesObject) => {
  if (!userRolesObject || typeof userRolesObject !== 'object') return [];
  
  return Object.entries(userRolesObject).map(([userId, roletype]) => ({
    userId,
    roletype
  }));
};

/**
 * Converts userRoles array to a simple object (for internal use in components)
 * @param {Array} userRolesArray - Array of {userId, username, roletype} objects
 * @returns {Object} - Object with username as key and role as value
 */
export const userRolesArrayToObject = (userRolesArray) => {
  if (!Array.isArray(userRolesArray)) return {};
  
  return userRolesArray.reduce((acc, ur) => {
    // Use username if available (backend now provides it), otherwise fall back to userId
    const key = ur.username || ur.userId;
    acc[key] = ur.roletype;
    return acc;
  }, {});
};

// ============================================================================
// USERNAME/ID NORMALIZATION FUNCTIONS
// ============================================================================

/**
 * Ensures accessMap has usernames (not MongoDB ObjectIds) for display purposes
 * This function normalizes accessMap by converting IDs to usernames where possible.
 * 
 * @param {Object} accessMap - The accessMap object to normalize
 * @param {Object} currentUser - Current user object with id and username properties
 * @returns {Object} - Normalized accessMap with usernames where possible
 * 
 * Note: This function can only convert the current user's ID to username.
 * Other users' IDs are preserved (backend will convert them when fetching).
 */
export const normalizeAccessMapUsernames = (accessMap = {}, currentUser) => {
  if (!accessMap) return accessMap;

  // Backend returns usernames in userRoles array, so we should preserve them
  // Only convert IDs to usernames if we have IDs (for backward compatibility)
  const userRolesArray = accessMap.userRoles || [];
  
  // Ensure all entries have valid userId and roletype
  const validUserRoles = userRolesArray.filter(ur => ur && ur.userId && ur.roletype);
  
  // If userRoles already contains usernames (not ObjectIds), preserve them as-is
  // ObjectIds are 24 hex characters, usernames are typically shorter and contain letters/dots
  const hasObjectIds = validUserRoles.some(ur => objectIdRegex.test(String(ur.userId).trim()));
  
  if (!hasObjectIds) {
    // Already has usernames, preserve them as-is
    // Ensure userRoleMap is created correctly
    const userRoleMap = userRolesArrayToObject(validUserRoles);
    return {
      ...accessMap,
      userRoleMap,
      userRoles: validUserRoles,
    };
  }

  // Has ObjectIds, but backend may also provide usernames
  // Build normalized map using usernames when available (for display)
  const normalizedMap = validUserRoles.reduce((acc, ur) => {
    // Use username if available (backend provides it), otherwise use userId
    const key = ur.username || ur.userId;
    if (key) {
      acc[String(key).trim()] = ur.roletype;
    }
    return acc;
  }, {});

  // Reconstruct userRoles array, using username as userId when available
  // This ensures userRolesArrayToObject will use usernames for keys
  const normalizedUserRoles = validUserRoles.map(ur => ({
    userId: ur.username || ur.userId, // Use username for userId if available
    username: ur.username, // Preserve username field
    roletype: ur.roletype,
    principalKey: ur.principalKey || ur.userId // Preserve principalKey if it exists
  }));

  return {
    ...accessMap,
    userRoleMap: normalizedMap,
    userRoles: normalizedUserRoles,
  };
};
